import axios from 'axios';
import type {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { AuthErrorCodes } from '../types/auth';
import { tokenStorage } from '../utils/tokenStorage';

// API configuration
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track ongoing refresh requests to prevent multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (error: any) => void;
}> = [];

// Process queued requests after token refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor - Add authentication token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const token = tokenStorage.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor - Handle token refresh and authentication errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Handle 401 errors with automatic token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't attempt token refresh for auth endpoints (login, register, etc.)
      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/auth/forgot-password') ||
        originalRequest.url?.includes('/auth/reset-password')
      ) {
        return Promise.reject(error);
      }

      // Check if this is a refresh token request that failed
      if (originalRequest.url?.includes('/auth/refresh')) {
        handleTokenRefreshFailure();
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = tokenStorage.getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Try to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        if (response.data.success && response.data.data) {
          const { accessToken, refreshToken: newRefreshToken } =
            response.data.data;

          // Update stored tokens (preserve remember me preference)
          tokenStorage.setTokens({
            accessToken,
            refreshToken: newRefreshToken,
          });

          // Update the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }

          // Process queued requests
          processQueue(null, accessToken);

          // Retry the original request
          return apiClient(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect
        processQueue(refreshError, null);
        handleTokenRefreshFailure();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other authentication-specific errors
    const errorData = error.response?.data as any;
    if (errorData?.error) {
      const errorCode = errorData.error;

      // Handle specific authentication error codes
      switch (errorCode) {
        case AuthErrorCodes.TOKEN_EXPIRED:
        case AuthErrorCodes.INVALID_TOKEN:
          // These should trigger token refresh (handled above) or logout
          if (!originalRequest._retry) {
            handleTokenRefreshFailure();
          }
          break;
        case AuthErrorCodes.ACCOUNT_LOCKED:
          // Account locked - user needs to contact support
          console.warn('Account is locked:', errorData.message);
          break;
        case AuthErrorCodes.INVALID_CREDENTIALS:
        case AuthErrorCodes.USER_NOT_FOUND:
          // Authentication failed - don't auto-retry
          break;
        default:
          // Other errors - let them bubble up
          break;
      }
    }

    // Handle 403 Forbidden errors (Limits & Activation)
    if (error.response?.status === 403) {
      const errorData = error.response.data as any;
      const msg = errorData?.message;
      if (msg === 'ACCOUNT_NOT_ACTIVATED') {
        if (typeof window !== 'undefined' && window.location.pathname !== '/activation') {
          window.location.href = '/activation';
        }
      } else if (
        msg === 'PROPERTY_LIMIT_REACHED' ||
        msg === 'STORAGE_LIMIT_REACHED' ||
        msg === 'INVALID_PLAN_TIER'
      ) {
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/limit-reached')) {
          const limitType = msg === 'STORAGE_LIMIT_REACHED' ? '?type=storage' : '';
          window.location.href = `/limit-reached${limitType}`;
        }
      }
    }

    return Promise.reject(error);
  },
);

// Handle token refresh failure
const handleTokenRefreshFailure = (): void => {
  // Clear tokens
  tokenStorage.clearTokens();

  // Reset refresh state
  isRefreshing = false;
  failedQueue = [];

  // Redirect to login if we're not already there
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// API response interface
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Generic API methods
export const api = {
  get: <T>(
    url: string,
    config?: { params?: Record<string, any> },
  ): Promise<AxiosResponse<ApiResponse<T>>> => apiClient.get(url, config),

  post: <T>(
    url: string,
    data?: unknown,
  ): Promise<AxiosResponse<ApiResponse<T>>> => apiClient.post(url, data),

  put: <T>(
    url: string,
    data?: unknown,
  ): Promise<AxiosResponse<ApiResponse<T>>> => apiClient.put(url, data),

  delete: <T>(url: string): Promise<AxiosResponse<ApiResponse<T>>> =>
    apiClient.delete(url),
};

export default apiClient;
